import {useState, useCallback} from 'react';

import {getSafely} from 'shared/web';

import {WizardStepConfig} from '../WizardStepConfig';

interface UseWizardKeyboardNavigationProps {
  steps: WizardStepConfig[];
  currentStep: number;
  onStepChange: (step: number) => void;
}

/**
 * Hook for handling keyboard navigation in wizard components
 *
 * Features:
 * - Arrow keys for navigating between wizard tabs
 * - Home/End keys for jumping to first/last tab
 * - Enter/Space for activating focused tab
 * - Tab key handling for leaving the tablist
 * - Focus tracking for roving tabindex pattern
 */
export function useWizardKeyboardNavigation({
  steps,
  currentStep,
  onStepChange,
}: UseWizardKeyboardNavigationProps) {
  const [focusedStep, setFocusedStep] = useState<number | null>(null);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const enabledSteps = steps
        .map((step, index) => ({...step, index}))
        .filter((step) => !step.isDisabled);
      const currentFocusIndex = focusedStep ?? currentStep;
      const currentEnabledIndex = enabledSteps.findIndex(
        (step) => step.index === currentFocusIndex
      );

      if (currentEnabledIndex === -1) {
        return;
      }

      const focusWizardStep = (stepIndex: number) => {
        // Use querySelectorAll to get all tabs and index them directly
        const allTabs = document.querySelectorAll('[role="tab"]');
        const stepElement = getSafely(allTabs, stepIndex) as HTMLElement;
        if (stepElement) {
          stepElement.focus();
          setFocusedStep(stepIndex);
        }
      };

      switch (event.key) {
        case 'Tab':
          // When Tab is pressed, we're leaving the tablist
          // Don't prevent default, but clear focus tracking
          setFocusedStep(null);
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault();
          // Normal navigation to previous step (cycling back from first to last)
          const prevIndex =
            currentEnabledIndex === 0 ? enabledSteps.length - 1 : currentEnabledIndex - 1;
          const prevStep = getSafely(enabledSteps, prevIndex);
          if (prevStep) {
            focusWizardStep(prevStep.index);
          }
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          event.preventDefault();
          {
            const nextIndex =
              currentEnabledIndex === enabledSteps.length - 1 ? 0 : currentEnabledIndex + 1;
            const nextStep = getSafely(enabledSteps, nextIndex);
            if (nextStep) {
              focusWizardStep(nextStep.index);
            }
          }
          break;
        case 'Home':
          event.preventDefault();
          const firstStep = enabledSteps[0];
          if (firstStep) {
            focusWizardStep(firstStep.index);
          }
          break;
        case 'End':
          event.preventDefault();
          const lastStep = enabledSteps[enabledSteps.length - 1];
          if (lastStep) {
            focusWizardStep(lastStep.index);
          }
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (focusedStep !== null && focusedStep !== currentStep) {
            onStepChange(focusedStep);
          }
          break;
      }
    },
    [steps, currentStep, focusedStep, onStepChange]
  );

  // Handle focus changes for roving tabindex
  const handleFocusIn = useCallback((event: React.FocusEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (target.getAttribute('role') === 'tab') {
      const tabIndex = target.getAttribute('data-wizard-tab-index');
      if (tabIndex !== null) {
        const index = parseInt(tabIndex, 10);
        setFocusedStep(index);
      }
    }
  }, []);

  return {
    focusedStep,
    handleKeyDown,
    handleFocusIn,
  };
}
